/**
 * The boot sequence (layered shell surfaces, D12, D18, D22, D23, R-8, R-9, R-27, WS-6 §3.8 and
 * §4). Framework-free: nothing in this module's static graph imports React.
 *
 * 1. Read the import-map manifest, emit the page's one map, import the runtime by its URL.
 * 2. Create the navigation watcher, the services, the loader host; install the host; wire the
 *    maintenance bridge into the API client.
 * 3. Ask the setup status: `setup` mounts the setup page at any path.
 * 4. Hydrate the session. A system path mounts its page; a signed-out visitor elsewhere is sent
 *    to `/login?returnTo=`; a signed-in one gets the root the assignment names.
 * 5. Keep reacting to navigation, session changes and system outputs until unload.
 *
 * Kernel apps (WS-16a, design section 3.5, R-013, R-117): the reading of
 * `window.__ZION_KERNEL_ORIGIN__` picks one of three arms before step 1. `invalid` shows the fatal
 * screen `kernel-origin`. `off` runs the steps above unchanged. `on` sends sign-in, setup, the admin
 * paths and the passkeys page to the kernel origin, and redeems the code that comes back.
 *
 * Customer onboarding S5 (WS-15, R-062 as R-064 item 1 amends it): in the `off` arm the path
 * `/auth/handoff-callback` on a tenant host redeems the tenant hand-off code that the DP host put
 * in the URL fragment, with the verifier this tab kept (`redeemTenantHandoff` of
 * `tenant/tenant-handoff-callback.ts`).
 *
 * Forced password change (#1172, D6 of plan `2026-10-21-forced-password-change`): every adopt site
 * navigates through `adoptedLandingOf`, so a session with `forcePasswordChange: true` goes to
 * `/change-password?returnTo=<landing>`. A session whose token carries `fpc: true` sees only the
 * change-password, logout, setup and two password reset system pages (R-007). A root path, after a
 * `hydrate` too, goes to `/change-password?returnTo=<path+search+hash>`, and so does the consent
 * page. Every other system page goes to a bare `/change-password`.
 */
import { sessionResponseSchema } from '@zion/sdk-contracts';
import { coldStartAwareFetch, parseJwt } from '@zion/sdk-react/auth-core';
import { emitImportMap, runtimeUrl, type ImportMap } from '@zion/sdk-surfaces/importmap';
import type { SurfaceServicesProvider } from '@zion/sdk-surfaces/v1';
import {
  buildLoginRedirectPath,
  matchSystemRoute,
  readSystemOutput,
  resolvePostLoginPath,
  type SessionPresence,
  type SystemOutputName,
  type SystemOutputs,
  type SystemSurfaceName,
} from '@zion/shell-system-surfaces/protocol';
import { TenantCallbackError, isTenantHandoffCallbackPath, redeemTenantHandoff } from '../tenant/tenant-handoff-callback.js';
import { appRegistrySnapshot, forgetAppRegistry, subscribeAppRegistry } from '../api/app-registry-snapshot.js';
import { getApps, initApiClient } from '../api/client.js';
import type { AppRegistryEntry } from '../api/types.js';
import { beginKernelSignIn, isKernelCallbackPath, redeemKernelCallback, type HandoffDeps, type HandoffStorage } from '../kernel/kernel-handoff.js';
import {
  kernelUrl,
  readKernelOrigin,
  setActiveKernelOrigin,
  type KernelOrigin,
  type KernelOriginGlobals,
  type KernelOriginReading,
} from '../kernel/kernel-origin.js';
import { isKernelOnlyEntry, kernelDestinationOf } from '../kernel/kernel-paths.js';
import {
  REACT_ADAPTER_SPECIFIER,
  createNavigationWatcher,
  loadImportMapManifest,
  loadSurfacesRuntime,
  systemClock,
  type ExtensionPointSource,
  type ModuleImporter,
  type NavigationWatcher,
  type ServiceSession,
  type SurfaceServicesHost,
  type SurfacesRuntime,
} from '@zion/surface-host';
import type { FatalReason } from './fatal-screen.js';
import { renderFatalScreen } from './fatal-screen.js';
import {
  bundleMissingMessage,
  createLoaderHost,
  surfaceBundleOf,
  type InterimRootAsset,
  type LegacyAdapter,
  type LoaderHost,
} from './loader-host.js';
import { createMaintenanceBridge } from './maintenance-bridge.js';
import { createMountController, type MountController, type MountedState } from './mount-controller.js';
import { prepareDocument, type TelemetryGlobals } from './prepare.js';
import { resolveRootAssignment, selectRootSurface } from './root-resolution.js';
import { createRuntimeEventLog } from './runtime-event-log.js';
import { createLoaderSession, createServiceSession, type LoaderSession, type SessionState } from './session.js';
import { createShellExtensionPointSource, createShellServices } from './shell-services.js';
import { readSetupMode, type SetupGateVerdict } from './setup-gate.js';
import { buildSystemInput } from './system-input.js';

/**
 * The sentence the tenant host login page shows after a hand-off code that did not redeem
 * (customer onboarding S5, WS-15, R-064 item 2). It names no failure reason.
 */
export const TENANT_HANDOFF_FAILED_MESSAGE = 'The sign-in could not move to this address. Sign in again to continue.';

/** Where a failed tenant redeem goes: the login page of this host, with {@link TENANT_HANDOFF_FAILED_MESSAGE}. */
export const TENANT_HANDOFF_FAILED_PATH = `/login?${new URLSearchParams({ error: TENANT_HANDOFF_FAILED_MESSAGE }).toString()}`;

/**
 * Where the boot sends a tenant redeem that failed with `error`. The record's landing is `landing`
 * when the redeem read it and a later step failed (a `SessionPayloadError` from `adopt`), else the
 * `returnTo` of a `TenantCallbackError`. Where that landing is a safe landing other than the default
 * one, the answer is the login page with {@link TENANT_HANDOFF_FAILED_MESSAGE} and
 * `returnTo=<landing>`, so the next sign-in lands where the person asked. With no landing, and with a
 * landing that `resolvePostLoginPath` would change or that is the default landing, the answer is
 * {@link TENANT_HANDOFF_FAILED_PATH}. Pure.
 */
export function tenantHandoffFailedPath(error: unknown, landing: string | null = null): string {
  const returnTo = landing ?? (error instanceof TenantCallbackError ? error.returnTo : null);
  if (returnTo === null || resolvePostLoginPath(returnTo) !== returnTo || returnTo === resolvePostLoginPath(null)) {
    return TENANT_HANDOFF_FAILED_PATH;
  }
  return `/login?${new URLSearchParams({ error: TENANT_HANDOFF_FAILED_MESSAGE, returnTo }).toString()}`;
}

/** The three elements `index.html` carries in body order (R-9). The loader creates any that are missing. */
export const LOADER_ELEMENT_IDS = Object.freeze(['root', 'zion-overlay', 'zion-maintenance'] as const);

/** The meta tags the build writes into `index.html`. */
export const INTERIM_ROOT_META = 'zion-interim-root';
export const INTERIM_ROOT_CSS_META = 'zion-interim-root-css';
export const SHELL_VERSION_META = 'zion-shell-version';

/**
 * What the kernel arms of `boot` run against (kernel apps, WS-16a, design section 3.5).
 *
 * - `reading`: the reading of `window.__ZION_KERNEL_ORIGIN__`, from `readKernelOrigin`.
 * - `leave`: send the document to an absolute URL on the kernel origin; the browser binds
 *   `location.replace`, so the `<dp>` entry leaves the history.
 * - `storage`: the tab's `sessionStorage`. It holds two records under two keys: the kernel handoff
 *   record (`HANDOFF_STORAGE_KEY` of `kernel/kernel-handoff.ts`) and the tenant hand-off record
 *   (`TENANT_HANDOFF_STORAGE_KEY`), which `routeTenantCallback` reads.
 * - `crypto`: the page's `Crypto`, for the PKCE pair.
 * - `now`: the clock in epoch milliseconds.
 */
export interface KernelEnvironment {
  readonly reading: KernelOriginReading;
  readonly leave: (url: string) => void;
  readonly storage: HandoffStorage;
  readonly crypto: Crypto;
  readonly now: () => number;
}

/** What `boot` runs against. `browserEnvironment()` binds the real document. */
export interface BootEnvironment {
  readonly window: Window;
  readonly document: Document;
  readonly fetch: typeof fetch;
  readonly importModule: ModuleImporter;
  readonly session: LoaderSession;
  readonly interimRoot: InterimRootAsset | null;
  readonly shellVersion: string;
  /**
   * Build the services provider over the session port, the watcher and the extension point
   * source; called once, after `#zion-overlay` exists. The browser binds `createShellServices`
   * (`@zion/surface-host` over the shell's fetch policy, WS-16d) and passes `extensionPoints` to it
   * (surface extension points, WS-7, R-011). The boot builds the source with
   * `createShellExtensionPointSource` over `appRegistrySnapshot`, `subscribeAppRegistry`, the
   * session port and the host it installs.
   *
   * The answer carries `dispose()`, which ends every mount's services and the provider's
   * subscription to `extensionPoints`. The boot calls it once on an abort of `signal`, and on the
   * `fatal: runtime` path when a later step of the services and host setup throws (push review
   * plausible row 2, R-020). So no registry listener of an ended boot outlives it.
   */
  readonly createServices: (
    session: ServiceSession,
    history: NavigationWatcher,
    extensionPoints: ExtensionPointSource,
  ) => SurfaceServicesProvider & Pick<SurfaceServicesHost, 'dispose'>;
  /** Telemetry and the deployment brand, run beside the manifest read; must never reject. */
  readonly prepare: () => Promise<void>;
  /**
   * Stops the loader reacting: on abort it disposes the services provider, then the navigation
   * watcher, and stops listening to the session. Absent in the browser, where the loader reacts
   * until unload.
   */
  readonly signal?: AbortSignal;
  /** The kernel origin reading and the parts the kernel arms use (WS-16a). */
  readonly kernel: KernelEnvironment;
}

/**
 * What `#root` held once the first routing settled: the root, the interim root, a system page or
 * the fatal screen. `left`: the boot sent the document to `url` on the kernel origin and routes no
 * more (WS-16a).
 */
export type BootOutcome = Exclude<MountedState, { readonly kind: 'booting' }> | { readonly kind: 'left'; readonly url: string };

/**
 * What the boot does with one system output that `readSystemOutput` accepted (WS-16a, R-117).
 *
 * - `adopt`: adopt `session` when it is not `null`, then go to `adoptedLandingOf(session, landing)`:
 *   `landing`, or the change-password page for a session that must change its password (#1172).
 *   `setup` is true for `setup-completed`, which re-reads the setup status.
 * - `sign-out`: end the session, then go to `landing`, or to the kernel `/logout` in the `on` arm.
 * - `drop`: ignore the output and write one `console.warn` line that names `reason`.
 */
export type SystemOutputAction =
  | { readonly kind: 'adopt'; readonly session: unknown; readonly landing: string; readonly setup: boolean }
  | { readonly kind: 'sign-out'; readonly landing: string }
  | { readonly kind: 'drop'; readonly reason: string };

/**
 * The action for `output`, the checked payload of the system output `name`.
 *
 * 1. `second-factor-required` answers `drop` in both arms. The shell has no second-factor page, so
 *    it adopts nothing and never signs out (R-117, E-28). The shell subscribes to
 *    `SYSTEM_OUTPUT_NAMES`, so the runtime refuses that emit first; this arm holds if a later list
 *    adds it.
 * 2. `sign-out-requested` answers `sign-out` with its landing.
 * 3. With `kernelOn`, `session-established` and `setup-completed` answer `drop`: only the code
 *    exchange gives the shell a session.
 * 4. Else they answer `adopt` with the session and the landing.
 *
 * Pure.
 */
export function systemOutputActionOf(
  name: SystemOutputName,
  output: SystemOutputs[SystemOutputName],
  kernelOn: boolean,
): SystemOutputAction {
  if ('wait' in output) return { kind: 'drop', reason: 'the shell has no second-factor page' };
  if (!('session' in output)) return { kind: 'sign-out', landing: output.landing };
  if (kernelOn) return { kind: 'drop', reason: 'only the kernel code exchange gives the shell a session' };
  return { kind: 'adopt', session: output.session, landing: output.landing, setup: name === 'setup-completed' };
}

/** The system path of the page where a person sets a new password (`SYSTEM_ROUTES` of the system surfaces). */
const CHANGE_PASSWORD_PATH = '/change-password';

/**
 * Where the boot goes after it adopted `session`, when the output or the redeem named `landing`
 * (forced password change, #1172, D6 of plan `2026-10-21-forced-password-change`).
 *
 * The boot calls it at each of its three adopt sites:
 *
 * 1. `handleOutput`, after a system page emitted `session-established` or `setup-completed`;
 * 2. the kernel callback redeem;
 * 3. the tenant hand-off redeem.
 *
 * Each site calls it only after `adopt` returned, so the session is in the store.
 *
 * Inputs: `session` is the body the site adopted, or `null` for a `setup-completed` that minted no
 * session. `landing` is a safe same-origin path: `readSystemOutput` or `resolvePostLoginPath`
 * checked it first.
 *
 * The answer, per input class:
 *
 * 1. `session` passes `sessionResponseSchema` and its `forcePasswordChange` is `true`: the answer
 *    of {@link forcedTargetOf} for `landing` (R-007). That is `landing` unchanged for a page of
 *    {@link FORCED_SESSION_PAGES} (the change-password page included, so no `returnTo` nests), a
 *    bare `/change-password` for every other system page except the consent page, and else
 *    `/change-password?returnTo=<landing>`, with `landing` percent-encoded as one query value.
 *    `ChangePasswordPage` reads `returnTo` and lands there after the change.
 * 2. `forcePasswordChange` is `false` or absent, `session` is `null`, or `session` fails the
 *    schema: `landing` unchanged.
 *
 * The server holds the control: core-api refuses a flagged session on every route outside the
 * change-password allowlist with 403 `password_change_required`. This answer only shows the page
 * that clears the flag. Pure: no I/O, never throws.
 */
export function adoptedLandingOf(session: unknown, landing: string): string {
  const parsed = sessionResponseSchema.safeParse(session);
  if (!parsed.success || parsed.data.forcePasswordChange !== true) return landing;
  return forcedTargetOf(landing);
}

/**
 * True when `accessToken` carries the claim `fpc: true` (`DataPlaneJwtPayload.fpc`), which
 * core-api writes into every token it mints while the user must change the password (#1172, D3).
 * The boot reads it for a session that reached a page without an adopt site: a `hydrate`
 * through the refresh cookie, a refresh or a peer tab. It verifies no signature: the claim picks a
 * page and authorizes nothing, and core-api's `PasswordChangeGuard` holds the control. A token
 * whose payload does not decode to an object answers `false`. Pure; never throws.
 */
function tokenMustChangePassword(accessToken: string): boolean {
  try {
    return parseJwt(accessToken).fpc === true;
  } catch {
    return false;
  }
}

/**
 * True when `state` is a `signed-in` session whose access token carries `fpc: true`
 * ({@link tokenMustChangePassword}). A `pending` or `signed-out` state answers `false`. Pure; never
 * throws.
 */
function sessionMustChangePassword(state: SessionState): boolean {
  return state.status === 'signed-in' && tokenMustChangePassword(state.accessToken);
}

/**
 * The system pages a session that must change its password may show (R-007 of plan
 * `2026-10-21-forced-password-change`, which amends R-005):
 *
 * - the change-password page;
 * - the logout page, so the person can sign out;
 * - the setup page, which mounts on the `setupMode` arm of `route` before this set is read, so an
 *   open setup never loops;
 * - the two password reset pages, because under KernelGuard `enforce` the forced change ends
 *   through the email reset (R-002).
 *
 * The set matches `FORCED_SESSION_PAGES` of the kernel shell (`kernel-boot.ts`), so the two shells
 * show one page for one state.
 */
const FORCED_SESSION_PAGES: ReadonlySet<SystemSurfaceName> = new Set<SystemSurfaceName>([
  'change-password',
  'logout',
  'setup',
  'password-reset-request',
  'password-reset-confirm',
]);

/**
 * The system pages a session that must change its password goes back to after the change (R-007):
 * the consent page, a page of a signed-in person. Every other system page outside
 * {@link FORCED_SESSION_PAGES} (a sign-in, recovery, callback, one-time token or not-found page)
 * travels in no `returnTo`, so the change lands on the default landing. The set matches
 * `RETURN_AFTER_CHANGE_PAGES` of the kernel shell.
 */
const RETURN_AFTER_CHANGE_PAGES: ReadonlySet<SystemSurfaceName> = new Set<SystemSurfaceName>(['oauth-consent']);

/**
 * Where a session that must change its password goes in place of `target`, a same-origin path with
 * an optional query and fragment (R-007). The path is the text of `target` before its first `?` or
 * `#`, and `matchSystemRoute` names its page, so case and one trailing slash do not count.
 *
 * 1. The path is a page of {@link FORCED_SESSION_PAGES}: `target` unchanged. A change-password
 *    target never nests a second `returnTo`.
 * 2. The path is no system page, or a page of {@link RETURN_AFTER_CHANGE_PAGES}:
 *    `/change-password?returnTo=<target>`, with `target` percent-encoded as one query value, the
 *    query and the fragment included.
 * 3. The path is any other system page: the bare `/change-password`.
 *
 * Pure; never throws.
 */
function forcedTargetOf(target: string): string {
  const system = matchSystemRoute(target.replace(/[?#][\s\S]*$/, ''));
  if (system === null || RETURN_AFTER_CHANGE_PAGES.has(system)) {
    return `${CHANGE_PASSWORD_PATH}?returnTo=${encodeURIComponent(target)}`;
  }
  return FORCED_SESSION_PAGES.has(system) ? target : CHANGE_PASSWORD_PATH;
}

/**
 * Where the boot sends a session that must change its password from the root-path location
 * `pathname`, `search` and `hash` (R-005 item 1): the change-password page with that location as
 * its one percent-encoded `returnTo`, the fragment included. The location passes
 * `resolvePostLoginPath` first, so an unsafe path gives `/home`. Pure; never throws.
 */
function forcedLocationLandingOf(pathname: string, search: string, hash: string): string {
  return forcedTargetOf(resolvePostLoginPath(`${pathname}${search}${hash}`));
}

/** The element with `id`, created and appended to `body` when absent: a `<main>` for `root` (R-76), else a `<div>`. */
function ensureElement(doc: Document, id: string): HTMLElement {
  const found = doc.getElementById(id);
  if (found !== null) return found;
  // `#root` is the document's `<main>` landmark, as `index.html` writes it (R-76).
  const el = doc.createElement(id === 'root' ? 'main' : 'div');
  el.id = id;
  doc.body.append(el);
  return el;
}

/** The two adapter functions, when the imported namespace carries both. */
function adapterOf(ns: Readonly<Record<string, unknown>> | null): LegacyAdapter | null {
  if (ns === null) return null;
  const { isLegacyAppModule, adaptLegacyApp } = ns;
  if (typeof isLegacyAppModule !== 'function' || typeof adaptLegacyApp !== 'function') return null;
  return {
    isLegacyAppModule: isLegacyAppModule as LegacyAdapter['isLegacyAppModule'],
    adaptLegacyApp: adaptLegacyApp as LegacyAdapter['adaptLegacyApp'],
  };
}

/**
 * `host` with a `resolve` that answers `null` for a target whose registry row is kernel-only
 * (kernel apps, WS-16a, design sections 3.8 and 5, finding WS-16a-R3).
 *
 * The boot installs this wrapper in the `on` arm. `registry` is the host's own registry read. When
 * the row whose `appId` equals `target.app` passes `isKernelOnlyEntry`, `resolve` answers `null`, so
 * the runtime fails the mount `unresolved` and imports no bundle. That holds for every mount path:
 * a routed pane, an embedded mount from a hosted bundle, a `legacy.v1` mount (R-129) and a root.
 * Every other target, the system target and the interim root included, goes to `host.resolve`
 * unchanged.
 *
 * `implementers(point)` answers `host.implementers(point)` without each entry whose app is
 * kernel-only by the same rule, as a new frozen array (surface extension points, D10, WS-7). So a
 * mount by point never picks a kernel-only implementer, and `services.extensions` never lists one.
 *
 * Both members read the first registry row of the app alone, as `createLoaderHost` does: a later
 * row with the same `appId` (a twin) decides nothing. So `implementers` lists an entry exactly when
 * `resolve` of its app arm does not refuse it as kernel-only (push review row 8, R-020).
 * The other members are the members of `host`. No side effect of its own.
 */
function kernelGuardedHost(host: LoaderHost, registry: () => readonly AppRegistryEntry[]): LoaderHost {
  /** True when the first registry row of `app` is kernel-only; false for no app or an app with no row. */
  const kernelOnly = (app: string | undefined): boolean => {
    const row = registry().find((entry) => entry.appId === app);
    return row !== undefined && isKernelOnlyEntry(row);
  };
  return {
    ...host,
    async resolve(target) {
      if (kernelOnly(target.app)) return null;
      return host.resolve(target);
    },
    implementers(point) {
      return Object.freeze(host.implementers(point).filter((entry) => !kernelOnly(entry.app)));
    },
  };
}

function presenceOf(state: SessionState): SessionPresence {
  if (state.status === 'pending') return 'pending';
  return state.status === 'signed-in' ? 'present' : 'absent';
}

/**
 * The boot outcome a controller's state reads as. A controller still `booting` reads as
 * `fatal: root-mount`: once routing has settled every path has shown something, so a boot that
 * shows nothing is a boot that stopped. Pure.
 */
export function bootOutcomeOf(controller: Pick<MountController, 'shown'>): Exclude<BootOutcome, { readonly kind: 'left' }> {
  const { shown } = controller;
  return shown.kind === 'booting' ? { kind: 'fatal', reason: 'root-mount' } : shown;
}

/**
 * Run the boot sequence against `env` and resolve with the first settled mount.
 *
 * Never rejects: a manifest failure (fetch, JSON, `ImportMapError`, a map with no
 * `@zion/sdk-surfaces-react/v1` entry, an existing map) renders `fatal: manifest`; a runtime
 * import failure, a window another loader still watches, a services setup that throws, or a host
 * install refusal renders `fatal: runtime` (on the last two the boot disposes the provider when
 * `createServices` returned one, then the watcher);
 * a system bundle that fails renders `fatal: system-bundle`; a root that fails renders
 * `fatal: root-mount`; a root surface for which `surfaceBundleOf` answers `null` (no entry for its
 * `uiPackage` in its row's `bundles`, R-129) renders `fatal: manifest`
 * with one console line naming the app, surface and package, and never imports a bundle (R-102);
 * anything unexpected renders `fatal: root-mount`. After it resolves the
 * loader keeps routing on every navigation, every session change and every system output:
 * - a system path mounts its page (the same page updates in place); once it settles, the session
 *   takes back the drivers the interim root held (`LoaderSession.reclaimDrivers`, architecture-4);
 * - leaving a system path for a root path resolves the root again; moves between root paths
 *   remount nothing (Q3);
 * - a `signed-out` session under a root replaces the URL with `/login?returnTo=`;
 * - a root path while the session is still `pending` (the hydrate hold) routes nowhere; the
 *   routing that follows `hydrate` handles that path once the session settles (architecture-5);
 * - each output passes `readSystemOutput` first and a refused payload is dropped.
 * An abort of `env.signal` disposes the services provider (every mount's services end and its
 * registry listener goes), then the watcher and the session listener, and routing stops.
 *   `session-established` adopts the session (a `SessionPayloadError` drops the event) and
 *   replaces the URL with `landing`; `setup-completed` adopts a non-null session, re-reads the
 *   setup status, hydrates the session through the refresh cookie when the payload carried none
 *   and the status no longer reads `setup`, and replaces the URL; `sign-out-requested` awaits
 *   `signOut` and replaces the URL.
 * The loader host resolves every mount against `appRegistrySnapshot()`, the last registry any
 * `getApps` call answered; a failed root-resolution read clears it. The provider's extension point
 * source reads the same snapshot and the installed host, so `services.extensions` answers what a
 * mount by point would mount, and it hears each registry change and each permission change
 * (surface extension points, D10, WS-7).
 *
 * The kernel arms (WS-16a, design section 3.5), picked from `env.kernel.reading` before the
 * manifest step:
 * - `invalid`: `setActiveKernelOrigin(null)`, the fatal screen `kernel-origin`, and nothing else.
 *   The shell never guesses a host.
 * - `off`: `setActiveKernelOrigin(null)`, then every step above runs as before the kernel origin.
 *   `leave` never runs.
 * - `on`: `setActiveKernelOrigin(origin)`. Setup mode leaves to the kernel `/` (OQ-7). Each route
 *   then runs these arms in order: the callback path redeems the code (`redeemKernelCallback`),
 *   adopts the session and replaces the URL with the landing, and a failure shows `sign-in`; a
 *   path that `kernelDestinationOf` answers `leave` for leaves; a `sign-in` answer starts a kernel
 *   sign-in and leaves; a system path that stays on `<dp>` mounts its page; a `pending` session
 *   routes nowhere; a `signed-out` session starts a kernel sign-in back to the path and leaves; a
 *   `signed-in` session resolves the root. `sign-out-requested` ends the session and leaves to the
 *   kernel `/logout`; an output that carries a session is dropped with one `console.warn` line.
 *   A failed sign-in start shows `sign-in`. Once a route in this document saw `signed-in`, a
 *   sign-in that would start while the session reads `signed-out` (the `/login` path or the
 *   `signed-out` arm) leaves to the kernel `/logout` instead. So a sign-out that bypassed
 *   `sign-out-requested` ends the kernel session and never signs the user in again (WS-16a-R1).
 *   The loader host refuses a kernel-only row (`isKernelOnlyEntry`) on every mount path. A root
 *   selection that names one mounts the system `not-found` page with one `console.warn` line
 *   (WS-16a-R3).
 * To leave, the boot records the URL and calls `env.kernel.leave` once. No later route and no later
 * output runs, and `boot` resolves `{ kind: 'left', url }`. In every arm `second-factor-required`
 * is dropped with one `console.warn` line (R-117).
 *
 * The tenant hand-off arm (customer onboarding S5, WS-15, R-062 and R-064) runs in the `off` arm
 * only, before the system routes, on `/auth/handoff-callback` (`isTenantHandoffCallbackPath`). It
 * passes `location.hash` to `redeemTenantHandoff` over `env.fetch`, with `env.kernel.storage` (the
 * tab's `sessionStorage`, which holds the record of the hand-off this tab started) and
 * `env.kernel.now`. It adopts the session and replaces the URL with the landing, so the fragment
 * leaves the address bar and the history entry. A failure (a `TenantCallbackError` from the redeem,
 * or a `SessionPayloadError` from `adopt`) writes one `console.warn` line and replaces the URL with
 * {@link tenantHandoffFailedPath}: the login page with the fixed sentence, and the record's landing
 * when the redeem read one. The replace also drops the fragment. In the `on` arm the path mounts
 * the system `not-found` page, as before.
 */
export async function boot(env: BootEnvironment): Promise<BootOutcome> {
  const doc = env.document;
  const [rootEl, , maintenanceEl] = LOADER_ELEMENT_IDS.map((id) => ensureElement(doc, id)) as [
    HTMLElement,
    HTMLElement,
    HTMLElement,
  ];

  const fatalBeforeController = (reason: FatalReason): BootOutcome => {
    renderFatalScreen(rootEl, reason);
    return { kind: 'fatal', reason };
  };

  const { reading } = env.kernel;
  if (reading.kind === 'invalid') {
    setActiveKernelOrigin(null);
    return fatalBeforeController('kernel-origin');
  }
  const kernelOrigin: KernelOrigin | null = reading.kind === 'on' ? reading.origin : null;
  setActiveKernelOrigin(kernelOrigin);
  /** The kernel URL the boot left for, or `null` while the document stays. */
  let left: string | null = null;
  const leave = (url: string): void => {
    left = url;
    env.kernel.leave(url);
  };

  const prepared = env.prepare();
  let map: ImportMap;
  let adapterUrl: string;
  try {
    map = await loadImportMapManifest(env.fetch);
    // `parseImportMapManifest` already refuses a map with no runtime entry (DP-008). The system
    // bundle also leaves the React adapter as a bare import (`SERVED_SPECIFIERS` in
    // `system-surfaces/src/build/externals.ts`), so a map without it fails the first system mount
    // late and without a reason. Refuse it here, before the page carries the map.
    const adapterEntry = Object.hasOwn(map.imports, REACT_ADAPTER_SPECIFIER) ? map.imports[REACT_ADAPTER_SPECIFIER] : undefined;
    if (adapterEntry === undefined) return fatalBeforeController('manifest');
    adapterUrl = adapterEntry;
    emitImportMap(map, doc);
  } catch {
    return fatalBeforeController('manifest');
  }
  let runtime: SurfacesRuntime;
  try {
    runtime = await loadSurfacesRuntime(runtimeUrl(map), env.importModule);
  } catch {
    return fatalBeforeController('runtime');
  }
  await prepared;

  let watcher: NavigationWatcher;
  try {
    watcher = createNavigationWatcher(env.window);
  } catch {
    // Another loader still watches this window: a second boot in one document.
    return fatalBeforeController('runtime');
  }
  let controller: MountController;
  let adapter: LegacyAdapter | null = null;
  let setupMode: SetupGateVerdict = 'ready';
  /** The services provider once `createServices` answered; the `fatal: runtime` path disposes it. */
  let provider: ReturnType<BootEnvironment['createServices']> | null = null;
  try {
    const serviceSession = createServiceSession(env.session);
    // The provider is built before the host it serves, so the source reads the host lazily: it
    // answers no implementer until the install below succeeds (surface extension points, WS-7).
    let installedHost: LoaderHost | null = null;
    const extensionPoints = createShellExtensionPointSource({
      registry: appRegistrySnapshot,
      registryChanges: subscribeAppRegistry,
      session: serviceSession,
      host: () => installedHost,
    });
    provider = env.createServices(serviceSession, watcher, extensionPoints);
    const loaderHost = createLoaderHost({
      // One registry for the host and the interim root's pane: the last `getApps` answer, whoever
      // asked for it (architecture-1). The pane's own read on each token change refreshes it.
      registry: appRegistrySnapshot,
      session: serviceSession,
      provider,
      legacyAdapter: () => adapter,
      interimRoot: env.interimRoot,
      shellVersion: env.shellVersion,
      // Runtime events carry no value, payload, URL or body (WS-2), so the console may show them.
      // The log writes the first 20 `surface:css-rejected` events per stylesheet, then one count
      // line (R-96). The sink reads `console.warn` at each call.
      onRuntimeEvent: createRuntimeEventLog((...args: unknown[]) => console.warn(...args)),
    });
    // The `on` arm refuses a kernel-only row on every mount path, not in `AppHost` alone (WS-16a-R3).
    const host = kernelOrigin === null ? loaderHost : kernelGuardedHost(loaderHost, appRegistrySnapshot);
    runtime.installSurfaceHost(host);
    installedHost = host;
    controller = createMountController({
      rootEl,
      maintenanceEl,
      doc,
      runtime,
      host,
      interimRoot: env.interimRoot,
      systemInput: (surface, brand, maintenance) =>
        buildSystemInput(surface, env.window.location, brand, presenceOf(env.session.current()), maintenance),
      onSystemOutput: (name, payload) => {
        void handleOutput(name, payload);
      },
    });
    initApiClient(
      createMaintenanceBridge(
        (notice) => controller.showMaintenance(notice),
        () => controller.hideMaintenance(),
      ),
    );
  } catch {
    // The provider subscribed to the module-level registry listeners at build: end it, or that
    // listener outlives this boot (R-020).
    provider?.dispose();
    watcher.dispose();
    return fatalBeforeController('runtime');
  }
  const services = provider;

  /**
   * Show a system page in `#root`, then take the session drivers back. A swap away from the
   * interim root unmounts its `AuthProvider`, which stops the refresh, visibility and cross-tab
   * drivers; without the reclaim a signed-in system page hears no peer sign-out (architecture-4).
   * The session ignores the call when it has not yielded.
   */
  const showSystemPage = async (surface: SystemSurfaceName): Promise<void> => {
    await controller.showSystem(surface);
    env.session.reclaimDrivers();
  };

  const handoffDeps = (origin: KernelOrigin): HandoffDeps => ({
    storage: env.kernel.storage,
    crypto: env.kernel.crypto,
    fetch: env.fetch,
    now: env.kernel.now,
    kernelOrigin: origin,
    returnHost: env.window.location.hostname,
  });

  /**
   * True once a route in this document saw a `signed-in` session (WS-16a-R1). The `on` arm reads it
   * before it starts a kernel sign-in: a session that ended here must end the kernel session too.
   */
  let heldSession = false;

  /**
   * Start a kernel sign-in back to `returnTo` and leave; a refused start shows `sign-in`.
   *
   * When this document held a signed-in session and the session now reads `signed-out`, the boot
   * leaves to the kernel `/logout` and starts no sign-in (WS-16a-R1). The shell session ended
   * without the `sign-out-requested` output: the interim root's own sign-out, a peer tab, or a
   * revoked family. A handoff now would meet the live kernel session and sign the user in again.
   */
  const signInAt = async (origin: KernelOrigin, returnTo: string): Promise<void> => {
    if (heldSession && env.session.current().status === 'signed-out') {
      leave(kernelUrl(origin, '/logout'));
      return;
    }
    let url: string;
    try {
      url = await beginKernelSignIn(handoffDeps(origin), returnTo);
    } catch (err) {
      console.warn('[shell] the kernel sign-in could not start', err);
      controller.showFatal('sign-in');
      return;
    }
    leave(url);
  };

  /** The `on` arms that run before the system routes; true when one of them took the path. */
  const routeKernel = async (origin: KernelOrigin, pathname: string, search: string): Promise<boolean> => {
    if (isKernelCallbackPath(pathname)) {
      try {
        const redeemed = await redeemKernelCallback(handoffDeps(origin), search);
        env.session.adopt(redeemed.session);
        watcher.navigate(adoptedLandingOf(redeemed.session, redeemed.landing), true);
      } catch (err) {
        // A `KernelHandoffError` from the redeem, or a `SessionPayloadError` from `adopt`.
        console.warn('[shell] the kernel sign-in did not finish', err);
        controller.showFatal('sign-in');
      }
      return true;
    }
    const destination = kernelDestinationOf(origin, pathname, search);
    if (destination === null) return false;
    if (destination.kind === 'leave') leave(destination.url);
    else await signInAt(origin, destination.returnTo);
    return true;
  };

  /**
   * Redeem the tenant hand-off code in the fragment against the record this tab wrote, adopt the
   * session and go to the record's landing.
   */
  const routeTenantCallback = async (hash: string): Promise<void> => {
    /** The record's landing once the redeem read it, so a later failure keeps it. */
    let landing: string | null = null;
    try {
      const redeemed = await redeemTenantHandoff({ fetch: env.fetch, storage: env.kernel.storage, now: env.kernel.now }, hash);
      landing = redeemed.landing;
      env.session.adopt(redeemed.session);
      watcher.navigate(adoptedLandingOf(redeemed.session, redeemed.landing), true);
    } catch (err) {
      console.warn('[shell] the tenant hand-off did not finish', err);
      watcher.navigate(tenantHandoffFailedPath(err, landing), true);
    }
  };

  const route = async (): Promise<void> => {
    if (left !== null || controller.kind === 'fatal') return;
    if (setupMode === 'setup') {
      await showSystemPage('setup');
      return;
    }
    const { pathname, search, hash } = env.window.location;
    if (kernelOrigin === null && isTenantHandoffCallbackPath(pathname)) {
      await routeTenantCallback(hash);
      return;
    }
    if (kernelOrigin !== null && (await routeKernel(kernelOrigin, pathname, search))) return;
    const system = matchSystemRoute(pathname);
    if (system !== null) {
      // A session that must change its password sees only the pages of FORCED_SESSION_PAGES, as
      // the kernel shell's route rule does (R-007). Every other system page goes to the
      // change-password page: the consent page with this location as `returnTo`, the rest bare.
      if (!FORCED_SESSION_PAGES.has(system) && sessionMustChangePassword(env.session.current())) {
        watcher.navigate(forcedTargetOf(`${pathname}${search}${hash}`), true);
        return;
      }
      await showSystemPage(system);
      return;
    }
    const current = env.session.current();
    // A session still hydrating is neither signed in nor signed out. The hold after a transient
    // refresh failure lasts up to `LOADER_BOOT_HOLD_MS`; a navigation inside it waits for the
    // `schedule()` that follows `hydrate`, so a visitor who turns out signed in is never sent to
    // the sign-in page (review finding architecture-5).
    if (current.status === 'pending') return;
    if (current.status !== 'signed-in') {
      if (kernelOrigin === null) watcher.navigate(buildLoginRedirectPath(pathname, search), true);
      else await signInAt(kernelOrigin, resolvePostLoginPath(`${pathname}${search}`));
      return;
    }
    heldSession = true;
    // A session that must change its password and came in through no adopt site (a `hydrate`, a
    // refresh, a peer tab) goes to the change-password page, as `adoptedLandingOf` sends an adopted
    // one (#1172, D6). The page is a system route, so this arm never sees it.
    if (tokenMustChangePassword(current.accessToken)) {
      watcher.navigate(forcedLocationLandingOf(pathname, search, hash), true);
      return;
    }
    if (controller.kind === 'root' || controller.kind === 'interim-root') return;
    const [assignment, apps, adapterNs] = await Promise.all([
      resolveRootAssignment(env.fetch, pathname),
      getApps().catch((): AppRegistryEntry[] => {
        forgetAppRegistry();
        return [];
      }),
      env.importModule(adapterUrl).catch(() => null),
    ]);
    adapter = adapterOf(adapterNs);
    const selection = selectRootSurface(apps, assignment, env.interimRoot !== null);
    if (selection.kind === 'surface') {
      // The `on` arm never mounts a kernel-only row as the root (WS-16a-R3). The interim root is no
      // fallback: the host resolves it only when the registry holds no row of its app.
      if (kernelOrigin !== null && isKernelOnlyEntry(selection.entry)) {
        console.warn(`[shell] root shell unavailable: ${selection.entry.appId} runs only on the kernel origin`);
        await showSystemPage('not-found');
        return;
      }
      // A root with no bundle is a manifest defect, as a missing import-map entry is (R-102,
      // DP-008): the fatal screen, never `not-found`. The answer is the row's bundle for the root's
      // `uiPackage` alone; no row-level bundle stands in for it (R-129).
      if (surfaceBundleOf(selection.entry, selection.declaration) === null) {
        console.warn(bundleMissingMessage(selection.entry.appId, selection.declaration));
        controller.showFatal('manifest');
        return;
      }
      await controller.showRoot(selection.target);
    } else if (selection.kind === 'interim') {
      env.session.yieldDrivers();
      await controller.showInterimRoot();
    } else {
      console.warn(`[shell] root shell unavailable: ${selection.reason}`);
      await showSystemPage('not-found');
    }
  };

  let chain: Promise<void> = Promise.resolve();
  const schedule = (): Promise<void> => {
    chain = chain.then(route).catch((err: unknown) => {
      console.error('[shell] routing failed', err);
      controller.showFatal('root-mount');
    });
    return chain;
  };
  const settle = async (): Promise<void> => {
    let seen: Promise<void>;
    do {
      seen = chain;
      await seen;
    } while (seen !== chain);
  };

  const handleOutput = async (name: SystemOutputName, payload: unknown): Promise<void> => {
    if (left !== null) return;
    const output = readSystemOutput(name, payload);
    if (output === null) {
      console.warn(`[shell] dropped a malformed '${name}' output`);
      return;
    }
    const action = systemOutputActionOf(name, output, kernelOrigin !== null);
    if (action.kind === 'drop') {
      console.warn(`[shell] dropped a '${name}' output: ${action.reason}`);
      return;
    }
    if (action.kind === 'sign-out') {
      await env.session.signOut();
      // The `on` arm ends the kernel session too, so the next handoff cannot sign the user in again.
      if (kernelOrigin !== null) leave(kernelUrl(kernelOrigin, '/logout'));
      else watcher.navigate(action.landing, true);
      return;
    }
    if (action.session !== null) {
      try {
        env.session.adopt(action.session);
      } catch {
        // `adopt` throws `SessionPayloadError` alone, and the store is unchanged.
        console.warn(`[shell] dropped a '${name}' output whose session did not parse`);
        return;
      }
    }
    if (action.setup) {
      setupMode = await readSetupMode(env.fetch);
      // A wizard that minted no session may still have set the refresh cookie: read it now, as
      // the boot's session step does (design S05, §4 step 10).
      if (setupMode !== 'setup' && action.session === null) await env.session.hydrate();
    }
    watcher.navigate(adoptedLandingOf(action.session, action.landing), true);
  };

  watcher.subscribe(() => {
    void schedule();
  });
  const offSession = env.session.subscribe((state) => {
    controller.refreshSystem();
    if (state.status === 'signed-out' && (controller.kind === 'root' || controller.kind === 'interim-root')) {
      void schedule();
    }
  });
  env.signal?.addEventListener(
    'abort',
    () => {
      services.dispose();
      watcher.dispose();
      offSession();
    },
    { once: true },
  );

  setupMode = await readSetupMode(env.fetch);
  if (kernelOrigin !== null && setupMode === 'setup') {
    // Setup mints an owner session, so the wizard runs on the kernel origin (OQ-7).
    leave(kernelUrl(kernelOrigin, '/'));
  } else {
    if (setupMode !== 'setup') await env.session.hydrate();
    void schedule();
    await settle();
  }
  return left === null ? bootOutcomeOf(controller) : { kind: 'left', url: left };
}

/** The content of `<meta name=<name>>`, or `null`. */
function metaContent(doc: Document, name: string): string | null {
  const content = doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content');
  return content === undefined || content === null || content === '' ? null : content;
}

/**
 * The interim root's two URLs from the `zion-interim-root` and `zion-interim-root-css` meta tags,
 * or `null` when either is absent or empty.
 */
export function readInterimRoot(doc: Document): InterimRootAsset | null {
  const bundleUrl = metaContent(doc, INTERIM_ROOT_META);
  const cssUrl = metaContent(doc, INTERIM_ROOT_CSS_META);
  return bundleUrl === null || cssUrl === null ? null : { bundleUrl, cssUrl };
}

/** The native dynamic import, kept out of the bundler's sight so a runtime URL stays a runtime URL. */
const nativeImport: ModuleImporter = (url) => import(/* @vite-ignore */ url) as Promise<Readonly<Record<string, unknown>>>;

/**
 * `inner` held to the caller's `init.signal` for the whole call (review finding devops-4).
 *
 * auth-core's `coldStartAwareFetch` waits between attempts for the larger of its backoff and the
 * answer's `Retry-After`, and it caps `Retry-After` at 120 s. Its default wait reads the signal and
 * ends at once on an abort. This wrapper also rejects with `signal.reason` as soon as the signal
 * aborts, as the native `fetch` does, whatever `inner` does. So each boot read keeps its own bound (`SETUP_STATUS_TIMEOUT_MS`, `ROOT_SHELL_FETCH_TIMEOUT_MS`,
 * `IMPORT_MAP_FETCH_TIMEOUT_MS`). A call with no signal goes to `inner` unchanged. A signal that
 * has already aborted rejects at once and sends nothing. The inner call that the abort overtakes
 * keeps its own rejection handled, so it raises no unhandled rejection.
 */
function signalBoundFetch(inner: typeof fetch): typeof fetch {
  return (input, init) => {
    const signal = init?.signal;
    if (signal === undefined || signal === null) return inner(input, init);
    if (signal.aborted) return Promise.reject(signal.reason);
    return new Promise<Response>((resolve, reject) => {
      const onAbort = (): void => reject(signal.reason);
      signal.addEventListener('abort', onAbort, { once: true });
      inner(input, init)
        .then(resolve, reject)
        .finally(() => signal.removeEventListener('abort', onAbort));
    });
  };
}

/**
 * The real environment: `window`, `document`, the global `fetch` behind auth-core's
 * `coldStartAwareFetch` (a GET that meets a cold 502, 504 or plain 503 is sent again, at most four
 * more times, and a keyless mutation is sent once; R-68 and `zion/no-raw-fetch-in-apps`). The
 * caller's `init.signal` bounds the whole call, the backoff sleeps included: an abort rejects
 * with the signal's reason at once (devops-4). Then a
 * `@vite-ignore` dynamic import, a fresh `createLoaderSession()`, the interim root from the meta tags, the shell version
 * from `zion-shell-version` (`0.0.0` when absent), `createShellServices` (WS-16d) over
 * `#zion-overlay` and the boot's extension point source (WS-7), and `prepareDocument` over the `runtime-config.js` globals. `kernel` binds
 * `readKernelOrigin(window)`, `location.replace`, the tab's `sessionStorage` read at each call,
 * `window.crypto` and `Date.now` (WS-16a).
 */
export function browserEnvironment(): BootEnvironment {
  const win = window;
  const doc = document;
  return {
    window: win,
    document: doc,
    fetch: signalBoundFetch(coldStartAwareFetch()),
    importModule: nativeImport,
    session: createLoaderSession(),
    interimRoot: readInterimRoot(doc),
    shellVersion: metaContent(doc, SHELL_VERSION_META) ?? '0.0.0',
    createServices: (session, history, extensionPoints) =>
      createShellServices({
        session,
        history,
        overlay: ensureElement(doc, 'zion-overlay'),
        window: win,
        clock: systemClock,
        extensionPoints,
      }),
    prepare: () => prepareDocument(win as Window & TelemetryGlobals),
    kernel: {
      reading: readKernelOrigin(win as Window & KernelOriginGlobals),
      leave: (url) => win.location.replace(url),
      storage: tabStorage(win),
      crypto: win.crypto,
      now: () => Date.now(),
    },
  };
}

/**
 * The tab's `sessionStorage`, read at each call. A browser that refuses the storage (a sandboxed
 * frame, disabled storage) throws on the read of `window.sessionStorage`; this wrapper moves that
 * throw from the boot into the handoff step, which maps it to `storage` or `no-record`.
 */
function tabStorage(win: Window): HandoffStorage {
  return {
    getItem: (key) => win.sessionStorage.getItem(key),
    setItem: (key, value) => win.sessionStorage.setItem(key, value),
    removeItem: (key) => win.sessionStorage.removeItem(key),
  };
}
